import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  Box,
  TextField,
  Grid,
  Paper,
  FormControl,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Map as MapIcon } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import * as yup from "yup";
import { getAllEmirates } from "../../../../api/emirates";
import { getRoutesByEmirates } from "../../../../api/routes";
import { getBustopByRouteId } from "../../../../api/bustop";

const ChangeRequest = ({ control, errors, setValue }) => {
  const [emirates, setEmirates] = useState([]);
  const [oldRoutes, setOldRoutes] = useState([]);
  const [newRoutes, setNewRoutes] = useState([]);
  const [oldBusStops, setOldBusStops] = useState([]);
  const [newBusStops, setNewBusStops] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BASE_API_URL || process.env.REACT_APP_BASE_API || "http://localhost:8000/api/";

  const fetchEmirates = async () => {
    try {
      const data = await getAllEmirates();
      setEmirates(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchRoutesByEmirates = async (emirateId, setRoutes, fieldNamePrefix) => {
    if (setValue && fieldNamePrefix) {
      setValue(`${fieldNamePrefix}Route`, "");
      setValue(`${fieldNamePrefix}BusStop`, "");
    }
    setRoutes([]);
    if (!emirateId) return;

    try {
      const data = await getRoutesByEmirates(emirateId);
      if (data && data.status && Array.isArray(data.data)) {
        setRoutes(data.data);
      } else if (Array.isArray(data)) {
        setRoutes(data);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error("Error fetching routes:", error);
      setRoutes([]);
    }
  };

  const fetchBustopByRouteId = async (routeId, setBusStops, fieldName) => {
    if (setValue && fieldName) {
      setValue(fieldName, "");
    }
    setBusStops([]);
    if (!routeId) return;

    try {
      const data = await getBustopByRouteId(routeId);
      if (data && data.status && Array.isArray(data.data)) {
        setBusStops(data.data);
      } else if (Array.isArray(data)) {
        setBusStops(data);
      } else {
        setBusStops([]);
      }
    } catch (error) {
      console.error("Error fetching bustop:", error);
      setBusStops([]);
    }
  };

  useEffect(() => {
    fetchEmirates();
  }, []);

  // const fetchRoutes = (emirateId, setRoutes) => {
  //   fetch(`http://localhost:8000/api/routes/by-emirate/${emirateId}`)
  //     .then((response) => response.json())
  //     .then((data) => {
  //       if (data.status && Array.isArray(data.data)) {
  //         setRoutes(data.data);
  //       } else {
  //         setRoutes([]);
  //       }
  //     })
  //     .catch((error) => console.error("Error fetching routes:", error));
  // };

  const fetchBusStops = (routeId, setBusStops) => {
    fetch(`${API_BASE_URL}bustops/route/${routeId}`, {
      headers: {
        "ngrok-skip-browser-warning": "true",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setBusStops(data.data);
        } else {
          setBusStops([]);
        }
      })
      .catch((error) => console.error("Error fetching bus stops:", error));
  };

  return (
    <Box sx={{ width: "75%", margin: "auto", mt: 3 }}>
      <Paper elevation={0} sx={{ border: "1px solid black", p: 2 }}>
        <Box display="flex" alignItems="center" sx={{ bgcolor: "#e8e8e8", p: 1, mb: 2 }}>
          <Typography variant="subtitle1" component="div" sx={{ fontWeight: "bold" }}>
            Please provide the details if change of residence and bus routes: (BUS OR STOP CHANGE
            REQUEST)
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Emirates
            </Typography>
            <Controller
              name="oldEmirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchRoutesByEmirates(event.target.value, setOldRoutes, "old");
                    }}
                    displayEmpty
                    error={!!errors.oldEmirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.oldEmirate && (
                    <Typography color="error" variant="caption">
                      {errors.oldEmirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Emirates
            </Typography>
            <Controller
              name="newEmirate"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="standard">
                  <Select
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                      fetchRoutesByEmirates(event.target.value, setNewRoutes, "new");
                    }}
                    displayEmpty
                    error={!!errors.newEmirate}
                    inputProps={{ "aria-label": "Select emirate" }}
                    sx={{
                      "&:before": { borderBottom: "1px solid black" },
                      "&:after": { borderBottom: "1px solid black" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select emirate
                    </MenuItem>
                    {emirates?.map((emirate) => (
                      <MenuItem key={emirate?.eid} value={emirate?.eid}>
                        {emirate?.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.newEmirate && (
                    <Typography color="error" variant="caption">
                      {errors.newEmirate.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Bus Route No.
            </Typography>
            <Controller
              name="oldRoute"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={oldRoutes}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.routename || ""} - ${option.routeno || ""}`
                      : ""
                  }
                  value={oldRoutes.find((r) => r.rid === field.value) || null}
                  onChange={(_, newValue) => {
                    const selectedVal = newValue ? newValue.rid : "";
                    field.onChange(selectedVal);
                    fetchBustopByRouteId(selectedVal, setOldBusStops, "oldBusStop");
                  }}
                  isOptionEqualToValue={(option, value) => option.rid === value.rid}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.rid}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>{`${option.routename} - ${option.routeno}`}</span>
                      {option.map && (
                        <Tooltip title="Show route on map" arrow>
                          <MapIcon
                            sx={{
                              marginLeft: "auto",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              color: "primary.main",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(option.map, "_blank");
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select route..."
                      error={!!errors.oldRoute}
                      helperText={errors.oldRoute?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Bus Route No.
            </Typography>
            <Controller
              name="newRoute"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={newRoutes}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.routename || ""} - ${option.routeno || ""}`
                      : ""
                  }
                  value={newRoutes.find((r) => r.rid === field.value) || null}
                  onChange={(_, newValue) => {
                    const selectedVal = newValue ? newValue.rid : "";
                    field.onChange(selectedVal);
                    fetchBustopByRouteId(selectedVal, setNewBusStops, "newBusStop");
                  }}
                  isOptionEqualToValue={(option, value) => option.rid === value.rid}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.rid}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>{`${option.routename} - ${option.routeno}`}</span>
                      {option.map && (
                        <Tooltip title="Show route on map" arrow>
                          <MapIcon
                            sx={{
                              marginLeft: "auto",
                              cursor: "pointer",
                              fontSize: "1.2rem",
                              color: "primary.main",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(option.map, "_blank");
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select route..."
                      error={!!errors.newRoute}
                      helperText={errors.newRoute?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Bus Stop:
            </Typography>
            <Controller
              name="oldBusStop"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={oldBusStops}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.pickupcode || ""} - ${option.bustopname || ""}`
                      : ""
                  }
                  value={oldBusStops.find((s) => s.sid === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.sid : "");
                  }}
                  isOptionEqualToValue={(option, value) => option.sid === value.sid}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.sid} sx={{ fontSize: "0.9rem" }}>
                      {`${option.pickupcode} - ${option.bustopname}`}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select old bus stop..."
                      error={!!errors.oldBusStop}
                      helperText={errors.oldBusStop?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Bus Stop:
            </Typography>
            <Controller
              name="newBusStop"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={newBusStops}
                  getOptionLabel={(option) =>
                    typeof option === "object" && option !== null
                      ? `${option.pickupcode || ""} - ${option.bustopname || ""}`
                      : ""
                  }
                  value={newBusStops.find((s) => s.sid === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.sid : "");
                  }}
                  isOptionEqualToValue={(option, value) => option.sid === value.sid}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.sid} sx={{ fontSize: "0.9rem" }}>
                      {`${option.pickupcode} - ${option.bustopname}`}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search and select new bus stop..."
                      error={!!errors.newBusStop}
                      helperText={errors.newBusStop?.message}
                      sx={{
                        "& .MuiInput-root": {
                          borderBottom: "1px solid black",
                          "&:before, &:after": { display: "none" },
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    sx: { maxHeight: 250, overflowY: "auto" },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              Old Location:
            </Typography>
            <Controller
              name="oldLocation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.oldLocation}
                  helperText={errors.oldLocation?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" component="label">
              New Location:
            </Typography>
            <Controller
              name="newLocation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="standard"
                  error={!!errors.newLocation}
                  helperText={errors.newLocation?.message}
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    "& .MuiInput-root": {
                      borderBottom: "1px solid black",
                      "&:before, &:after": { display: "none" },
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
          Note:- One month prior notice required for any route change and administrative charge AED
          100/- to be paid (Subject to seat and bus availability)
        </Typography>
      </Paper>
    </Box>
  );
};

ChangeRequest.validationSchema = yup.object().shape({
  oldEmirate: yup.string().required("Old Emirates is required"),
  newEmirate: yup.string().required("New Emirates is required"),
  oldRoute: yup.string().required("Old Bus Route No. is required"),
  newRoute: yup.string().required("New Bus Route No. is required"),
  oldBusStop: yup.string().required("Old Bus Stop is required"),
  newBusStop: yup.string().required("New Bus Stop is required"),
  oldLocation: yup.string().required("Old Location is required"),
  newLocation: yup.string().required("New Location is required"),
});

export default ChangeRequest;
